import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  ThumbsUp,
  Lightbulb,
  Bug,
  Sparkles,
  Search,
  Filter,
  Trash2,
  Edit,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format date from ISO to dd-mm-yy
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString().slice(-2);
  return `${day}-${month}-${year}`;
};

const typeConfig = {
  feature: { icon: Lightbulb, label: 'Fonctionnalité', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  bug: { icon: Bug, label: 'Bug / Erreur', color: 'bg-red-100 text-red-700 border-red-200' },
  improvement: { icon: Sparkles, label: 'Amélioration', color: 'bg-purple-100 text-purple-700 border-purple-200' }
};

const statusConfig = {
  submitted: { label: 'Soumis', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  reviewing: { label: 'En révision', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  planned: { label: 'Planifié', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  in_progress: { label: 'En cours', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  testing: { label: 'En test', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  implemented: { label: 'Implémenté', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-600 border-red-200' }
};

const roleLabels = {
  admin: 'Admin',
  consultant: 'Consultant',
  sme: 'PME',
  controleur: 'Contrôleur'
};

export const DevelopmentPage = () => {
  const { user } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, proposal: null });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  
  const [newProposal, setNewProposal] = useState({
    title: '',
    description: '',
    type: 'feature'
  });

  useEffect(() => {
    fetchProposals();
  }, []);

  const fetchProposals = async () => {
    try {
      const response = await axios.get(`${API}/proposals`);
      setProposals(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newProposal.title.trim() || !newProposal.description.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/proposals`, newProposal);
      toast.success('Proposition soumise !');
      setDialogOpen(false);
      setNewProposal({ title: '', description: '', type: 'feature' });
      fetchProposals();
    } catch (error) {
      toast.error('Erreur lors de la soumission');
    } finally {
      setSaving(false);
    }
  };

  const handleUpvote = async (proposalId) => {
    try {
      const response = await axios.post(`${API}/proposals/${proposalId}/upvote`);
      toast.success(response.data.action === 'added' ? 'Vote ajouté !' : 'Vote retiré');
      fetchProposals();
    } catch (error) {
      toast.error('Erreur lors du vote');
    }
  };

  const handleStatusChange = async (proposalId, newStatus) => {
    try {
      await axios.put(`${API}/proposals/${proposalId}`, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchProposals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (proposalId) => {
    try {
      await axios.delete(`${API}/proposals/${proposalId}`);
      toast.success('Proposition supprimée');
      fetchProposals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleEdit = async () => {
    if (!editDialog.proposal) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/proposals/${editDialog.proposal.id}`, {
        title: editDialog.proposal.title,
        description: editDialog.proposal.description
      });
      toast.success('Proposition mise à jour');
      setEditDialog({ open: false, proposal: null });
      fetchProposals();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const filteredProposals = proposals.filter(p => {
    const matchesSearch = p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || p.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    const matchesTab = activeTab === 'all' || 
                      (activeTab === 'mine' && p.author_id === user?.id) ||
                      activeTab === 'popular';  // Popular shows all, just sorted differently
    return matchesSearch && matchesType && matchesStatus && matchesTab;
  });

  // Sort by upvotes for popular tab (highest first)
  const sortedProposals = activeTab === 'popular' 
    ? [...filteredProposals].sort((a, b) => b.upvote_count - a.upvote_count)
    : filteredProposals;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="development-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Développement</h1>
            <p className="text-slate-500 mt-1">Proposez des fonctionnalités et signalez des bugs</p>
          </div>
          <Button onClick={() => setDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle proposition
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Lightbulb className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{proposals.length}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Lightbulb className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {proposals.filter(p => p.type === 'feature').length}
                  </p>
                  <p className="text-xs text-slate-500">Fonctionnalités</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Bug className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {proposals.filter(p => p.type === 'bug').length}
                  </p>
                  <p className="text-xs text-slate-500">Bugs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {proposals.filter(p => p.status === 'implemented').length}
                  </p>
                  <p className="text-xs text-slate-500">Implémentés</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs & Filters */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <TabsList>
              <TabsTrigger value="all">Toutes</TabsTrigger>
              <TabsTrigger value="mine">Mes propositions</TabsTrigger>
              <TabsTrigger value="popular">Populaires</TabsTrigger>
            </TabsList>
            
            <div className="flex flex-1 gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="feature">Fonctionnalité</SelectItem>
                  <SelectItem value="bug">Bug</SelectItem>
                  <SelectItem value="improvement">Amélioration</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="submitted">Soumis</SelectItem>
                  <SelectItem value="reviewing">En révision</SelectItem>
                  <SelectItem value="planned">Planifié</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="testing">En test</SelectItem>
                  <SelectItem value="implemented">Implémenté</SelectItem>
                  <SelectItem value="rejected">Refusé</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <TabsContent value={activeTab} className="mt-6">
            {/* Proposals Grid */}
            {sortedProposals.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <Lightbulb className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Aucune proposition trouvée</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setDialogOpen(true)}
                  >
                    Créer la première
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {sortedProposals.map((proposal) => {
                  const TypeIcon = typeConfig[proposal.type]?.icon || Lightbulb;
                  const hasVoted = proposal.upvotes?.includes(user?.id);
                  const isAuthor = proposal.author_id === user?.id;
                  const isAdmin = user?.role === 'admin';
                  
                  return (
                    <Card 
                      key={proposal.id} 
                      className={`border-slate-200 hover:shadow-md transition-shadow ${
                        proposal.status === 'implemented' ? 'bg-emerald-50/30' : ''
                      } ${proposal.status === 'rejected' ? 'opacity-60' : ''}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex gap-4">
                          {/* Upvote Section */}
                          <div className="flex flex-col items-center">
                            <Button
                              variant={hasVoted ? "default" : "outline"}
                              size="sm"
                              className={`h-12 w-12 rounded-lg ${hasVoted ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                              onClick={() => handleUpvote(proposal.id)}
                            >
                              <ThumbsUp className={`w-5 h-5 ${hasVoted ? 'fill-current' : ''}`} />
                            </Button>
                            <span className={`text-lg font-bold mt-1 ${hasVoted ? 'text-blue-600' : 'text-slate-600'}`}>
                              {proposal.upvote_count}
                            </span>
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className={typeConfig[proposal.type]?.color}>
                                  <TypeIcon className="w-3 h-3 mr-1" />
                                  {typeConfig[proposal.type]?.label}
                                </Badge>
                                {isAdmin ? (
                                  <Select 
                                    value={proposal.status} 
                                    onValueChange={(value) => handleStatusChange(proposal.id, value)}
                                  >
                                    <SelectTrigger className="h-6 text-xs w-auto border-0 p-0 px-2">
                                      <Badge variant="outline" className={statusConfig[proposal.status]?.color}>
                                        {statusConfig[proposal.status]?.label}
                                      </Badge>
                                    </SelectTrigger>
                                    <SelectContent>
                                      {Object.entries(statusConfig).map(([key, config]) => (
                                        <SelectItem key={key} value={key}>
                                          {config.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Badge variant="outline" className={statusConfig[proposal.status]?.color}>
                                    {statusConfig[proposal.status]?.label}
                                  </Badge>
                                )}
                              </div>
                              
                              {/* Actions */}
                              {(isAuthor || isAdmin) && (
                                <div className="flex items-center gap-1">
                                  {isAuthor && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      onClick={() => setEditDialog({ open: true, proposal: { ...proposal } })}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Supprimer cette proposition ?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Cette action est irréversible.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() => handleDelete(proposal.id)}
                                          className="bg-red-600 hover:bg-red-700"
                                        >
                                          Supprimer
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              )}
                            </div>
                            
                            <h3 className="text-lg font-semibold text-slate-900 mt-2">{proposal.title}</h3>
                            <p className="text-slate-600 mt-1 text-sm line-clamp-2">{proposal.description}</p>
                            
                            <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {proposal.author_name} ({roleLabels[proposal.author_role] || proposal.author_role})
                              </span>
                              <span>{formatDate(proposal.created_at)}</span>
                              {proposal.updated_at && (
                                <span className="text-slate-400">Modifié le {formatDate(proposal.updated_at)}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* New Proposal Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Nouvelle proposition</DialogTitle>
              <DialogDescription>
                Proposez une fonctionnalité, signalez un bug ou suggérez une amélioration
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select 
                  value={newProposal.type} 
                  onValueChange={(value) => setNewProposal({ ...newProposal, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="feature">
                      <div className="flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-blue-600" />
                        Fonctionnalité
                      </div>
                    </SelectItem>
                    <SelectItem value="bug">
                      <div className="flex items-center gap-2">
                        <Bug className="w-4 h-4 text-red-600" />
                        Bug / Erreur
                      </div>
                    </SelectItem>
                    <SelectItem value="improvement">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        Amélioration
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Titre *</Label>
                <Input
                  value={newProposal.title}
                  onChange={(e) => setNewProposal({ ...newProposal, title: e.target.value })}
                  placeholder="Résumé court de votre proposition"
                />
              </div>
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={newProposal.description}
                  onChange={(e) => setNewProposal({ ...newProposal, description: e.target.value })}
                  placeholder="Décrivez en détail votre proposition, le problème rencontré ou l'amélioration souhaitée..."
                  rows={5}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleCreate} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving ? 'Envoi...' : 'Soumettre'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Proposal Dialog */}
        <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, proposal: null })}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Modifier la proposition</DialogTitle>
            </DialogHeader>
            {editDialog.proposal && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Titre *</Label>
                  <Input
                    value={editDialog.proposal.title}
                    onChange={(e) => setEditDialog({ 
                      ...editDialog, 
                      proposal: { ...editDialog.proposal, title: e.target.value } 
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Textarea
                    value={editDialog.proposal.description}
                    onChange={(e) => setEditDialog({ 
                      ...editDialog, 
                      proposal: { ...editDialog.proposal, description: e.target.value } 
                    })}
                    rows={5}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog({ open: false, proposal: null })}>Annuler</Button>
              <Button onClick={handleEdit} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
